import {inject, injectable} from 'inversify';

import type {Expand, Expanded, MaybeExpanded} from '../../../../api/types';
import type {FileMapperJrpc} from '../../File/mappers/File.mapper.jrpc';
import type {SubtenantMapperJrpc} from '../../Subtenant/mappers/Subtenant.mapper.jrpc';
import type {UserMapperJrpc} from '../../User/mappers/User.mapper.jrpc';
import type {OfferMapperJrpc} from './Offer.mapper.jrpc';

import {extendExpand} from '../../../../api/utils/expand';
import {EnvProviderService} from '../../../system/env/EnvProvider.service';
import {BaseExpandJrpc} from '../../Base/mappers/Base.expand.jrpc';
import {SubtenantExpandJrpc} from '../../Subtenant/mappers/Subtenant.expand.jrpc';
import {UserExpandJrpc} from '../../User/mappers/User.expand.jrpc';

@injectable()
export class OfferExpandJrpc extends BaseExpandJrpc {
  constructor(
    @inject(EnvProviderService) env: EnvProviderService,
    @inject(UserExpandJrpc) private userExpand: UserExpandJrpc,
    @inject(SubtenantExpandJrpc) private subtenantExpand: SubtenantExpandJrpc,
  ) {
    super(env);
  }

  get public(): Expand {
    return {
      offer: ['avatar_id'],
    };
  }

  getByEnv(key?: BaseExpandJrpc.EnvKey, depth = 0): Expand {
    let base: Expand = {
      offer: [
        'avatar_id',
        [
          'client_id',
          {name: 'Client not found'},
          this.userExpand.getByEnv(key),
        ],
        [
          'concierge_id',
          {name: 'Concierge not found'},
          this.userExpand.getByEnv(key),
        ],
        [
          'employer_id',
          {name: 'Employer not found'},
          this.userExpand.getByEnv(key),
        ],
      ],
    };

    if (depth === 0) {
      base = extendExpand(base, [
        [
          'parent_offer_id',
          null,
          this.getByEnv(key, depth + 1),
          ['parent_offer_id'],
        ],
      ]);
    }

    const core: Expand = extendExpand(base, [
      [
        'available_for_subtenant_ids->subtenants',
        null,
        this.subtenantExpand.getByEnv(key),
      ],
    ]);
    const b2b: Expand = base;

    return this.resolve({b2b, core, key});
  }
}

export namespace OfferExpandJrpc {
  export type ExpandedFields = {
    avatar: MaybeExpanded<FileMapperJrpc.ApiData>;
    client?: Expanded<UserMapperJrpc.ExpandedApiData, 'name'>;
    concierge?: Expanded<UserMapperJrpc.ExpandedApiData, 'name'>;
    employer?: Expanded<UserMapperJrpc.ExpandedApiData, 'name'>;
    parent_offer?: MaybeExpanded<OfferMapperJrpc.ExpandedApiData>;
    subtenants?: MaybeExpanded<SubtenantMapperJrpc.ExpandedApiData>[];
  };
}
